import {
  FieldValues,
  Path,
  PathValue,
  useController,
  useFormContext,
  useFormState,
  useWatch,
} from 'react-hook-form'

import { FocusTarget } from '@/components/form/focus-target'
import { Select } from '@/components/ui/select'
import {
  SelectMultiProps,
  SelectOptionValue,
  SelectSingleProps,
  SelectTreeSingleProps,
} from '@/components/ui/select/select.types'

// Note: form fields don't support tree-checkbox mode (SelectTreeCheckboxProps) -
// its `value`/`onChange` shape and required `checkbox: true` discriminant don't
// fit this generic `type`-driven wrapper. Add dedicated support if a form ever
// needs a checkbox tree select.
type SelectFormFieldSelectProps<V extends SelectOptionValue> =
  | SelectSingleProps<V>
  | SelectMultiProps<V>
  | SelectTreeSingleProps<V>

type SelectFormFieldProps<TFieldValues extends FieldValues, V extends SelectOptionValue> = Omit<
  SelectFormFieldSelectProps<V>,
  'value' | 'onChange' | 'error'
> & {
  name: Path<TFieldValues>
}

export const SelectFormField = <
  TFieldValues extends FieldValues = FieldValues,
  V extends SelectOptionValue = string,
>({
  name,
  type,
  ...props
}: SelectFormFieldProps<TFieldValues, V>) => {
  const { setValue, getFieldState } = useFormContext<TFieldValues>()
  // Note: registers the field so RHF can focus it when invalid; the value still flows through setValue/useWatch
  const { field } = useController<TFieldValues>({ name })
  const formState = useFormState<TFieldValues>({ name })
  const { error, isTouched } = getFieldState(name, formState)
  const value = useWatch({ name }) ?? (type === 'multi' ? [] : null)
  const shouldShowError = isTouched || formState.isSubmitted

  // Note: built as one pre-typed object, rather than spread inline in the JSX
  // below, so TS checks it once against `SelectFormFieldSelectProps<V>` - mixing
  // a `...spread` with sibling literal props in JSX defeats discriminated-union
  // narrowing (TS checks the merged shape against the whole union at once and
  // can't tell which member is intended).
  const selectProps = {
    ...(props as SelectFormFieldSelectProps<V>),
    error: shouldShowError ? error?.message : undefined,
    name,
    onChange: (newValue: unknown) =>
      setValue(name, newValue as PathValue<TFieldValues, typeof name>, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      }),
    type,
    value,
  } as SelectFormFieldSelectProps<V>

  return (
    <FocusTarget fieldRef={field.ref}>
      <Select {...selectProps} />
    </FocusTarget>
  )
}
